// Apolo IDE — CostMeter (Sprint v4 Epic S5 WS S5.2).
//
// Live cost display + alert when 80% threshold is reached.
// PRE-RESOLVED defaults for v4:
//   - Live update real-time (2s poll via useCostMeter)
//   - Alert when 80% of monthly cap is reached
//
// Pure presentational. State is owned by useCostMeter.
//
// Accessibility:
//   - role="status" + aria-live so screen readers announce the threshold
//     transition without us implementing a separate toast.
//   - Color is not the only signal — at 80%+ we render "ALERT" text and an
//     AlertTriangle icon so users with reduced contrast or color blindness
//     still see the warning.

import { AlertTriangle, DollarSign } from 'lucide-react';

function fmtUsd(n, digits = 3) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '$0.00';
  if (n < 0.001) return '< $0.001';
  return `$${n.toFixed(digits)}`;
}

function pctColor(pct) {
  if (pct >= 0.8) return 'text-apolo-error';
  if (pct >= 0.6) return 'text-apolo-warning';
  return 'text-apolo-success';
}

function pctLabel(pct) {
  if (!Number.isFinite(pct)) return '0%';
  return `${(pct * 100).toFixed(0)}%`;
}

export default function CostMeter({ snapshot, loading, compact = false, className = '' }) {
  // Pre-load skeleton when we have no data yet but are about to.
  if (!snapshot) {
    return (
      <span
        data-testid="cost-meter-loading"
        role="status"
        aria-live="polite"
        aria-busy={loading ? 'true' : 'false'}
        className={`inline-flex items-center gap-1 font-mono text-[10px] text-apolo-textTertiary ${className}`}
      >
        <DollarSign size={10} aria-hidden="true" />
        <span>—</span>
      </span>
    );
  }

  const spend = Number(snapshot.monthlySpendUsd || 0);
  const cap = Number(snapshot.monthlyCapUsd || 0);
  const pct = Number(snapshot.pct || 0);
  const isAlert = !!snapshot.alert;
  const color = pctColor(pct);

  if (compact) {
    return (
      <span
        data-testid="cost-meter"
        role="status"
        aria-live="polite"
        aria-label={`Custo mensal ${pctLabel(pct)} (${fmtUsd(spend)} de ${fmtUsd(cap, 2)})`}
        className={`inline-flex items-center gap-1 font-mono text-[10px] ${color} ${className}`}
        data-alert={isAlert ? 'true' : 'false'}
      >
        {isAlert ? (
          <AlertTriangle size={10} aria-hidden="true" />
        ) : (
          <DollarSign size={10} aria-hidden="true" />
        )}
        <span>
          {fmtUsd(spend)} / {fmtUsd(cap, 2)}
        </span>
        <span aria-hidden="true">({pctLabel(pct)})</span>
      </span>
    );
  }

  return (
    <div
      data-testid="cost-meter"
      role="status"
      aria-live="polite"
      className={`flex flex-col gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface1 px-2 py-1 ${className}`}
      data-alert={isAlert ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] text-apolo-textTertiary">
          {isAlert ? (
            <AlertTriangle size={10} className="text-apolo-error" aria-hidden="true" />
          ) : (
            <DollarSign size={10} aria-hidden="true" />
          )}
          Custo mensal
        </span>
        <span className={`font-mono text-[10px] ${color}`}>{pctLabel(pct)}</span>
      </div>
      <div className="font-mono text-[11px] text-apolo-textPrimary">
        {fmtUsd(spend)} <span className="text-apolo-textTertiary">/ {fmtUsd(cap, 2)}</span>
      </div>
      <div
        aria-hidden="true"
        className="h-1 w-full overflow-hidden rounded bg-apolo-surface2"
      >
        <div
          data-testid="cost-meter-bar"
          className={`h-full transition-all duration-300 ${
            pct >= 0.8
              ? 'bg-apolo-error'
              : pct >= 0.6
                ? 'bg-apolo-warning'
                : 'bg-apolo-success'
          }`}
          style={{ width: `${Math.min(100, Math.max(0, pct * 100)).toFixed(1)}%` }}
        />
      </div>
      {isAlert && (
        <p data-testid="cost-meter-alert" className="text-[10px] text-apolo-error">
          ALERT: monthly cap atingiu 80%. Aproveite ou ajuste em Settings.
        </p>
      )}
      {snapshot.activeDispatchCount > 0 && (
        <p className="text-[10px] text-apolo-textTertiary">
          {snapshot.activeDispatchCount} dispatch{snapshot.activeDispatchCount === 1 ? '' : 'es'} ativos
          {' '}({fmtUsd(snapshot.activeDispatchCostUsd)})
        </p>
      )}
    </div>
  );
}
